// Turning Finance & Books on, and the settings that shape how it posts.
import { useState } from "react";
import { toast } from "sonner";
import { BookOpenCheck, CheckCircle2, AlertTriangle, Info } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { CAPABILITIES, RECORDS_NOTE, can, todayISO } from "../../lib/books";
import { Button, Field, Section, inputClass } from "./shared";

const CHECK_LABELS = {
  plan: "Plan",
  database_transactions: "Database transactions",
  deposit_classification: "Deposit classification",
  evidence_storage: "Receipt uploads",
};

export function EnableBooks({ status, onEnabled }) {
  const readiness = status?.readiness;
  const [goLive, setGoLive] = useState(todayISO());
  const [openingDate, setOpeningDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);
  const allowed = can(status, CAPABILITIES.configure);

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/finance/enable", {
        confirm: "ENABLE", go_live_date: goLive, opening_balance_date: openingDate,
      });
      toast.success("Finance & Books is on. Start with your opening balances.");
      onEnabled?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="books-enable">
      <Section
        title="Set up Finance & Books"
        description={"Double-entry records for your rental business: every peso you receive, hold, owe or spend, " +
                      "with reports your accountant can use."}
      >
        <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
          {[
            "Rental charges, payments and customer charges tied to each booking",
            "Security deposits held as money you owe back, never as income",
            "Advance payments applied to the rental charge, not mixed with deposits",
            "Expenses, vendor bills, driver pay and transfers between your accounts",
            "Trial balance, profit and loss, balance sheet, cash movement and exports",
          ].map((line) => (
            <li key={line} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {line}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Before you switch it on" testId="books-readiness">
        <div className="space-y-2">
          {(readiness?.checks || []).map((check) => (
            <div key={check.code}
                 className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${
                   check.ok ? "border-emerald-200 bg-emerald-50/60"
                            : check.blocking ? "border-rose-200 bg-rose-50/60" : "border-amber-200 bg-amber-50/60"}`}
                 data-testid={`books-check-${check.code}`}>
              {check.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                        : <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />}
              <div>
                <div className="font-medium text-slate-900">
                  {CHECK_LABELS[check.code] || check.code}
                  {!check.blocking && !check.ok && <span className="ml-2 text-xs text-slate-500">(optional)</span>}
                </div>
                <div className="text-slate-600">{check.message}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Books start date" hint="Activity from this date is recorded in the books.">
            <input type="date" className={inputClass} value={goLive} data-testid="books-go-live"
                   onChange={(e) => setGoLive(e.target.value)} />
          </Field>
          <Field label="Opening balance date"
                 hint="Usually the same day. Choose an earlier date only if you will also enter reviewed history.">
            <input type="date" className={inputClass} value={openingDate} data-testid="books-opening-date"
                   onChange={(e) => setOpeningDate(e.target.value)} />
          </Field>
        </div>
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <Info className="w-3.5 h-3.5 inline mr-1" />
          Switching Books on is permanent: posted entries are never edited or deleted, corrections are recorded as
          reversals, and your existing finance records become a read-only archive. {RECORDS_NOTE}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={enable} busy={busy} data-testid="books-enable-btn"
                  disabled={!allowed || !readiness?.can_enable}>
            <BookOpenCheck className="w-4 h-4" /> Switch on Finance & Books
          </Button>
          {!allowed && <span className="text-sm text-slate-500 self-center">Only the owner can switch this on.</span>}
        </div>
      </Section>
    </div>
  );
}

const MODE_OPTIONS = [
  { value: "not_configured", label: "Not decided yet" },
  { value: "additional", label: "Added on top of the rental price" },
  { value: "included", label: "Already included in the rental price" },
];

export function BooksSettings({ status, accounts, onSaved }) {
  const profile = status?.profile || {};
  const [form, setForm] = useState({
    driver_service_price_mode: profile.driver_service_price_mode || "not_configured",
    damage_recovery_policy: profile.damage_recovery_policy || "revenue",
    insurance_recovery_policy: profile.insurance_recovery_policy || "revenue",
    post_rental_policy: profile.post_rental_policy || "pass_through",
    price_includes_tax: profile.price_includes_tax || "unknown",
    auto_post_on_completion: !!profile.auto_post_on_completion,
    legal_name: profile.legal_name || "",
    method_accounts: { ...(profile.method_accounts || {}) },
  });
  const [busy, setBusy] = useState(false);
  const canConfigure = can(status, CAPABILITIES.configure);
  const payment = (accounts || []).filter((a) => a.is_payment_account && a.active);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/finance/settings", form);
      toast.success("Books settings saved.");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Books settings" description="How your business's own rules are applied when money is posted."
             testId="books-settings"
             actions={<Button onClick={save} busy={busy} disabled={!canConfigure} data-testid="books-settings-save">
                        Save settings
                      </Button>}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Driver service price"
               hint="Needed before a with-driver booking's charge can be posted.">
          <select className={inputClass} value={form.driver_service_price_mode} data-testid="books-driver-mode"
                  onChange={(e) => setForm({ ...form, driver_service_price_mode: e.target.value })}>
            {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Damage recovery" hint="Where money recovered for damage is recorded.">
          <select className={inputClass} value={form.damage_recovery_policy}
                  onChange={(e) => setForm({ ...form, damage_recovery_policy: e.target.value })}>
            <option value="revenue">As damage recovery income (4031)</option>
            <option value="cost_recovery">Against repair costs (5010)</option>
          </select>
        </Field>
        <Field label="Insurance recoveries">
          <select className={inputClass} value={form.insurance_recovery_policy}
                  onChange={(e) => setForm({ ...form, insurance_recovery_policy: e.target.value })}>
            <option value="revenue">As insurance recovery income (4050)</option>
            <option value="cost_recovery">Against repair costs (5010)</option>
          </select>
        </Field>
        <Field label="Tolls and fines recharged to the renter">
          <select className={inputClass} value={form.post_rental_policy}
                  onChange={(e) => setForm({ ...form, post_rental_policy: e.target.value })}>
            <option value="pass_through">Pass-through (no income)</option>
            <option value="revenue">As post-rental charges income (4038)</option>
          </select>
        </Field>
        <Field label="Do your prices include tax?" hint="Recorded as stated; ConfirmPH never splits out tax.">
          <select className={inputClass} value={form.price_includes_tax}
                  onChange={(e) => setForm({ ...form, price_includes_tax: e.target.value })}>
            <option value="unknown">Not confirmed</option>
            <option value="true">Prices include tax</option>
            <option value="false">Prices exclude tax</option>
          </select>
        </Field>
        <Field label="Registered / legal name (optional)">
          <input className={inputClass} value={form.legal_name} maxLength={200}
                 onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
        </Field>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 p-3">
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Where each payment method lands</div>
        <div className="mt-2 grid sm:grid-cols-2 gap-2">
          {["cash", "bank_transfer", "gcash", "maya", "card"].map((method) => (
            <Field key={method} label={method.replace("_", " ")}>
              <select className={inputClass} value={form.method_accounts?.[method] || ""}
                      data-testid={`books-method-${method}`}
                      onChange={(e) => setForm({
                        ...form,
                        method_accounts: { ...form.method_accounts, [method]: e.target.value || null },
                      })}>
                <option value="">Undeposited funds (flagged for review)</option>
                {payment.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
              </select>
            </Field>
          ))}
        </div>
      </div>

      <label className="mt-4 flex items-start gap-2 rounded-xl border border-slate-200 p-3">
        <input type="checkbox" className="mt-0.5" checked={form.auto_post_on_completion}
               data-testid="books-auto-post"
               onChange={(e) => setForm({ ...form, auto_post_on_completion: e.target.checked })} />
        <span className="text-sm text-slate-700">
          <span className="font-medium">Post rental charges automatically when a booking is completed.</span>
          <span className="block text-xs text-slate-500 mt-0.5">
            Owner-only. Charges with review flags are never posted automatically. Off by default.
          </span>
        </span>
      </label>
    </Section>
  );
}
